/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useUserProfileData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for loading user profile data.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/auth';
import { DEFAULT_USER_PROFILE_DATA } from '../utils';
import type { UserProfileData } from '../types';

/**
 * Helper function to construct full avatar URL
 */
const getFullAvatarUrl = (avatarPath: string | null): string | null => {
  if (!avatarPath) return null;
  return avatarPath.startsWith('http') ? avatarPath : `${avatarPath}`;
};

/**
 * Custom hook for fetching user profile data
 */
export function useUserProfileData() {
  const { user } = useAuthStore();
  const [formData, setFormData] = useState<UserProfileData>(DEFAULT_USER_PROFILE_DATA);
  const [loading, setLoading] = useState(true);
  const [avatar, setAvatar] = useState<string | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Load current user data
        setFormData({
          username: user.username || '',
          email: user.email || '',
          fullName: user.fullName || '',
          department: user.department || '',
          locale: user.locale || 'es',
          timezone: user.timezone || 'UTC',
          startupDialog: user.startupDialog || '/dashboard',
          startModuleUrl: user.startModuleUrl || undefined,
          debugModeActive: (user as any).debugModeActive || false
        });

        // Set existing avatar if available
        if (user.avatar) {
          setAvatar(getFullAvatarUrl(user.avatar));
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

  return {
    formData,
    setFormData,
    loading,
    avatar,
    setAvatar
  };
}

